import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trophy, Medal, Star, Shield, Award, Users } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BADGE_INFO = {
  first_report: { label: 'First Report', icon: Star, color: 'text-yellow-500' },
  active_citizen: { label: 'Active Citizen', icon: Shield, color: 'text-blue-500' },
  road_warrior: { label: 'Road Warrior', icon: Medal, color: 'text-purple-500' },
  traffic_champion: { label: 'Traffic Champion', icon: Award, color: 'text-red-500' },
};

const rankColors = [
  'from-yellow-400 to-amber-500',
  'from-gray-300 to-gray-400',
  'from-amber-600 to-amber-700',
];

export default function LeaderboardPage() {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/leaderboard`).then(r => {
      setLeaders(r.data.leaderboard || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-8" data-testid="leaderboard-page">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
          <Trophy className="w-5 h-5 text-secondary-foreground" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Leaderboard</h1>
          <p className="text-sm text-muted-foreground">Top contributors making Kalaburagi roads safer</p>
        </div>
      </div>

      {/* Top 3 Podium */}
      {leaders.length >= 3 && (
        <div className="grid grid-cols-3 gap-3 mb-8" data-testid="top-3-podium">
          {[1, 0, 2].map((idx, pos) => {
            const l = leaders[idx];
            if (!l) return null;
            const isFirst = idx === 0;
            return (
              <div key={l.id} className={`text-center ${isFirst ? 'order-2 -mt-4' : pos === 0 ? 'order-1 mt-4' : 'order-3 mt-4'}`}>
                <div className={`w-16 h-16 mx-auto rounded-full bg-gradient-to-br ${rankColors[idx]} flex items-center justify-center text-white text-xl font-bold shadow-lg mb-3 ${isFirst ? 'w-20 h-20 ring-4 ring-yellow-300/50' : ''}`}>
                  {idx + 1}
                </div>
                <p className="font-bold text-sm truncate">{l.name}</p>
                <p className="text-xs text-muted-foreground">{l.points} pts</p>
                <p className="text-xs text-muted-foreground">{l.reports_count} reports</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Full List */}
      <div className="space-y-2">
        {loading ? (
          <div className="text-center py-16 text-muted-foreground">Loading leaderboard...</div>
        ) : leaders.length === 0 ? (
          <div className="text-center py-16 space-y-4" data-testid="empty-leaderboard">
            <Users className="w-12 h-12 text-muted-foreground mx-auto" />
            <p className="text-muted-foreground">No contributors yet. Be the first to report!</p>
          </div>
        ) : (
          leaders.map((leader, i) => (
            <Card key={leader.id} className="hover:shadow-md transition-shadow duration-200 animate-fade-in-up" style={{ animationDelay: `${i * 0.03}s` }} data-testid={`leaderboard-entry-${i}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${i < 3 ? `bg-gradient-to-br ${rankColors[i]} text-white` : 'bg-muted text-muted-foreground'}`}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm truncate">{leader.name}</span>
                    {leader.area && <span className="text-xs text-muted-foreground hidden sm:inline">({leader.area})</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {(leader.badges || []).map(b => {
                      const info = BADGE_INFO[b];
                      if (!info) return null;
                      return (
                        <span key={b} className="flex items-center gap-0.5 text-xs" title={info.label}>
                          <info.icon className={`w-3 h-3 ${info.color}`} />
                        </span>
                      );
                    })}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-bold">{leader.points}</p>
                  <p className="text-xs text-muted-foreground">{leader.reports_count} reports</p>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
